/**
 * SpeedTest.jsx — public Speed Reader Challenge at /speed-test.
 *
 * Design intent: HUD/game overlay, not a card-and-form web page. Full-bleed
 * vertical layout, near-black background, red accent for danger, gold for
 * XP rewards. The flashed words use a serif display font so the test feels
 * literary; HUD overlays use the system sans.
 *
 * Anonymous users can play forever — XP just isn't credited until they
 * sign up. The endpoint POST /api/speed-test/submit handles both cases
 * (returns xp_award + credited:false + anonymous_cta when not logged in).
 *
 * If the user IS logged in and lands on this page from the post-signin
 * pop-up, we pass `?source=postlogin` so the close button takes them
 * straight back to the library instead of /signup.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Sparkles, Zap, Trophy, ChevronRight, X, ArrowLeft, RotateCw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Difficulty tiers — keep in lockstep with backend's DIFFICULTY_TIERS.
const DIFFICULTIES = [
  { id: 'easy',        label: 'Warm up',     range: [180, 260],  blurb: 'A relaxed jog through the words.', accent: 'emerald' },
  { id: 'normal',      label: 'Normal',      range: [280, 420],  blurb: 'Above-average reader pace.',         accent: 'sky' },
  { id: 'speed_demon', label: 'Speed Demon', range: [400, 650],  blurb: 'You need real focus.',               accent: 'amber' },
  { id: 'insane',      label: 'Insane',      range: [550, 950],  blurb: 'Most people miss what flashes.',      accent: 'orange' },
  { id: 'lightspeed',  label: 'Lightspeed',  range: [700, 1200], blurb: 'Pro speed-readers only.',             accent: 'rose' },
];

const SENTENCE_FALLBACK = [
  "Smarter readers level up faster than everyone else.",
  "Your next chapter is already waiting for you.",
  "Reading is the easiest habit you'll ever gamify.",
  "Every page you finish turns straight into XP.",
];

// Generates 2 decoy sentences that look believable. They start the same
// or close, then swap a key noun/verb so the user has to actually
// remember which one they saw.
const makeDecoys = (target, pool) => {
  const others = pool.filter((s) => s !== target);
  const decoys = [];
  for (let i = 0; i < 2 && others.length; i++) {
    const pick = others.splice(Math.floor(Math.random() * others.length), 1)[0];
    decoys.push(pick);
  }
  while (decoys.length < 2) {
    decoys.push(target.replace(/\.$/, ' — quickly.'));
  }
  return decoys;
};

const lerp = (a, b, t) => a + (b - a) * t;

const Stage = {
  INTRO: 'intro',
  COUNTDOWN: 'countdown',
  FLASH: 'flash',
  RECALL: 'recall',
  RESULT: 'result',
};

const SpeedTest = () => {
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const fromPostLogin = search.get('source') === 'postlogin';

  const [stage, setStage] = useState(Stage.INTRO);
  const [difficulty, setDifficulty] = useState('normal');
  const [pool, setPool] = useState(SENTENCE_FALLBACK);
  const [target, setTarget] = useState(SENTENCE_FALLBACK[0]);
  const [choices, setChoices] = useState([]);
  const [pickedIdx, setPickedIdx] = useState(null);
  const [countdown, setCountdown] = useState(3);
  const [flashIdx, setFlashIdx] = useState(0);
  const [liveWpm, setLiveWpm] = useState(0);
  const [result, setResult] = useState(null); // {wpm, badge, xp_award, credited}
  const [resultDisplay, setResultDisplay] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const isLoggedInRef = useRef(false); // surface for the result CTA
  const flashStartRef = useRef(0);

  // ---------- Boot: pull sentence bank + viewer-auth status ----------
  useEffect(() => {
    fetch(`${BACKEND_URL}/api/speed-test/sentences`)
      .then((r) => r.ok ? r.json() : null)
      .then((d) => { if (d?.sentences?.length) setPool(d.sentences); })
      .catch(() => { /* keep fallback */ });
    fetch(`${BACKEND_URL}/api/auth/me`, { credentials: 'include' })
      .then((r) => { isLoggedInRef.current = r.ok; })
      .catch(() => { isLoggedInRef.current = false; });
  }, []);

  // ---------- Stage: intro → countdown ----------
  const startTest = () => {
    const t = pool[Math.floor(Math.random() * pool.length)];
    setTarget(t);
    setChoices(shuffle([t, ...makeDecoys(t, pool)]));
    setPickedIdx(null);
    setResult(null); setResultDisplay(0);
    setFlashIdx(0); setLiveWpm(0);
    setCountdown(3);
    setStage(Stage.COUNTDOWN);
  };

  // ---------- Countdown effect ----------
  useEffect(() => {
    if (stage !== Stage.COUNTDOWN) return;
    if (countdown <= 0) { setStage(Stage.FLASH); flashStartRef.current = performance.now(); return; }
    const t = setTimeout(() => setCountdown((c) => c - 1), 700);
    return () => clearTimeout(t);
  }, [stage, countdown]);

  // ---------- Flash effect ----------
  const tier = useMemo(() => DIFFICULTIES.find((d) => d.id === difficulty) || DIFFICULTIES[1], [difficulty]);
  const words = useMemo(() => target.split(/\s+/), [target]);
  useEffect(() => {
    if (stage !== Stage.FLASH) return;
    if (flashIdx >= words.length) {
      // End of sentence — go to recall
      setStage(Stage.RECALL);
      return;
    }
    // Compute the wpm for THIS word position (lerp start → end).
    const t = words.length === 1 ? 1 : flashIdx / (words.length - 1);
    const wpm = lerp(tier.range[0], tier.range[1], t);
    setLiveWpm(Math.round(wpm));
    // ms per word at this wpm.
    const ms = Math.max(40, Math.round(60_000 / wpm));
    const timer = setTimeout(() => setFlashIdx((i) => i + 1), ms);
    return () => clearTimeout(timer);
  }, [stage, flashIdx, words, tier]);

  // ---------- Recall pick ----------
  const onRecallPick = async (idx) => {
    if (pickedIdx !== null) return;
    setPickedIdx(idx);
    const correct = choices[idx] === target;
    // Average WPM across the run = avg of (start, end).
    const avgWpm = Math.round((tier.range[0] + tier.range[1]) / 2);
    setSubmitting(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/speed-test/submit`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ wpm: avgWpm, difficulty, correct }),
      });
      const d = await r.json();
      setResult(d);
    } catch {
      setResult({ wpm: avgWpm, correct, badge: 'Speed Reader Unlocked', xp_award: 50, credited: false, anonymous_cta: 'Sign up to save your XP' });
    } finally {
      setSubmitting(false);
      // Brief delay so the green/red feedback registers before the result screen
      setTimeout(() => setStage(Stage.RESULT), 1100);
    }
  };

  // ---------- Result count-up animation ----------
  useEffect(() => {
    if (stage !== Stage.RESULT || !result) return;
    const target = result.wpm || 0;
    const start = performance.now();
    const dur = 1200;
    let raf;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / dur);
      setResultDisplay(Math.round(target * (1 - Math.pow(1 - t, 3))));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [stage, result]);

  // ---------- Render ----------
  return (
    <div
      className="min-h-screen w-full bg-[#0B0C10] text-[#EAE6DD] flex flex-col items-stretch overflow-x-hidden relative"
      style={{ fontFamily: '"Inter", system-ui, sans-serif' }}
      data-testid="speed-test-root"
    >
      {/* Top HUD bar */}
      <div className="px-5 py-4 flex items-center justify-between border-b border-white/5">
        <button
          type="button"
          onClick={() => fromPostLogin ? navigate('/profile') : navigate(-1)}
          className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-white/60 hover:text-white"
          data-testid="speed-test-back-btn"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          {fromPostLogin ? 'Skip' : 'Back'}
        </button>
        <span className="text-[10px] uppercase tracking-[0.3em] text-white/40">Speed Reader Challenge</span>
        <span className="w-12" />
      </div>

      {/* Stage: INTRO */}
      {stage === Stage.INTRO && (
        <div className="flex-1 flex flex-col items-center justify-center px-6 py-10 gap-8" data-testid="speed-test-intro">
          <div className="text-center max-w-md">
            <p className="text-[#FF3B30] font-black text-xs uppercase tracking-[0.4em] mb-3">Challenge</p>
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] text-white"
              style={{ fontFamily: '"Fraunces", "Playfair Display", Georgia, serif' }}
            >
              Can you read{' '}
              <span className="text-[#FF3B30]">900 words a minute?</span>
            </h1>
            <p className="text-sm text-white/60 mt-5">
              Average reader: <strong className="text-white/90">200–250 wpm</strong>.
              Speed readers hit <strong className="text-[#FFD60A]">900+</strong>.
              Pick your tier and we&apos;ll flash a sentence at you — then test if you actually read it.
            </p>
          </div>

          <div className="w-full max-w-md grid grid-cols-1 gap-2">
            {DIFFICULTIES.map((d) => {
              const active = difficulty === d.id;
              return (
                <button
                  key={d.id}
                  onClick={() => setDifficulty(d.id)}
                  className={`w-full text-left rounded-xl border px-4 py-3 transition ${
                    active
                      ? 'border-[#FF3B30] bg-[#FF3B30]/10'
                      : 'border-white/10 hover:border-white/30 bg-white/[0.02]'
                  }`}
                  data-testid={`speed-test-diff-${d.id}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-base font-bold">{d.label}</span>
                    <span className="text-xs font-mono tabular-nums text-white/50">{d.range[0]}–{d.range[1]} wpm</span>
                  </div>
                  <p className="text-xs text-white/50 mt-0.5">{d.blurb}</p>
                </button>
              );
            })}
          </div>

          <button
            onClick={startTest}
            className="bg-[#FF3B30] hover:bg-[#ff5046] text-white font-black px-8 py-4 rounded-full uppercase tracking-widest text-sm inline-flex items-center gap-2 shadow-[0_0_40px_rgba(255,59,48,0.4)]"
            data-testid="speed-test-start-btn"
          >
            Start the test <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Stage: COUNTDOWN */}
      {stage === Stage.COUNTDOWN && (
        <div className="flex-1 flex items-center justify-center" data-testid="speed-test-countdown">
          <div
            className="text-[#FF3B30] font-black text-9xl sm:text-[12rem] tabular-nums animate-pulse"
            style={{ fontFamily: '"Fraunces", Georgia, serif' }}
            key={countdown}
          >
            {countdown > 0 ? countdown : 'GO'}
          </div>
        </div>
      )}

      {/* Stage: FLASH */}
      {stage === Stage.FLASH && (
        <div className="flex-1 flex flex-col" data-testid="speed-test-flash">
          {/* Progress bar */}
          <div className="h-1.5 w-full bg-white/5">
            <div
              className="h-full bg-[#FF3B30] transition-[width] duration-100"
              style={{ width: `${(flashIdx / Math.max(1, words.length)) * 100}%` }}
            />
          </div>
          <div className="flex-1 flex items-center justify-center px-4">
            <span
              className="text-5xl sm:text-7xl md:text-8xl font-black text-white text-center break-words"
              style={{ fontFamily: '"Fraunces", Georgia, serif', letterSpacing: '-0.02em' }}
              data-testid="speed-test-current-word"
            >
              {words[flashIdx] || ''}
            </span>
          </div>
          {/* HUD: live wpm */}
          <div className="px-5 py-4 flex items-center justify-end">
            <div className="font-mono text-right">
              <div className="text-[10px] uppercase tracking-widest text-white/40">Live wpm</div>
              <div className="text-3xl font-black text-[#FFD60A] tabular-nums" data-testid="speed-test-live-wpm">{liveWpm}</div>
            </div>
          </div>
        </div>
      )}

      {/* Stage: RECALL */}
      {stage === Stage.RECALL && (
        <div className="flex-1 flex flex-col items-stretch justify-center px-5 py-8 gap-3" data-testid="speed-test-recall">
          <div className="text-center mb-3">
            <p className="text-[10px] uppercase tracking-widest text-white/40">Recall check</p>
            <h2 className="text-2xl font-black text-white mt-1">Which sentence did you just see?</h2>
          </div>
          {choices.map((s, i) => {
            const picked = pickedIdx === i;
            const isCorrect = s === target;
            let border = 'border-white/15 hover:border-white/40';
            if (pickedIdx !== null) {
              if (isCorrect) border = 'border-emerald-400 bg-emerald-500/10';
              else if (picked) border = 'border-rose-400 bg-rose-500/10';
              else border = 'border-white/10 opacity-60';
            }
            return (
              <button
                key={i}
                onClick={() => onRecallPick(i)}
                disabled={pickedIdx !== null}
                className={`w-full text-left rounded-2xl border px-4 py-4 transition ${border}`}
                style={{ fontFamily: '"Fraunces", Georgia, serif' }}
                data-testid={`speed-test-choice-${i}`}
              >
                <span className="text-base sm:text-lg text-white">{s}</span>
              </button>
            );
          })}
          {submitting && <p className="text-center text-xs text-white/50 mt-2">Scoring…</p>}
        </div>
      )}

      {/* Stage: RESULT */}
      {stage === Stage.RESULT && result && (
        <div className="flex-1 flex flex-col items-center justify-start px-5 py-8 gap-6" data-testid="speed-test-result">
          {/* Big wpm number */}
          <div className="text-center mt-2">
            <p className="text-[10px] uppercase tracking-[0.4em] text-white/40">Your score</p>
            <div
              className="text-7xl sm:text-8xl font-black tabular-nums text-white mt-1"
              style={{ fontFamily: '"Fraunces", Georgia, serif' }}
            >
              {resultDisplay}
              <span className="text-2xl sm:text-3xl text-white/40 ml-2">wpm</span>
            </div>
            <p className={`text-sm mt-1 ${result.correct ? 'text-emerald-300' : 'text-rose-300'}`}>
              {result.correct ? '✓ Recall correct' : '✗ Recall wrong — half XP'}
            </p>
          </div>

          {/* Comparison bars */}
          <div className="w-full max-w-md space-y-2" data-testid="speed-test-comparison-bars">
            {[
              { label: 'You',           wpm: result.wpm,  color: '#FF3B30' },
              { label: 'Average reader', wpm: 250,         color: '#6B7280' },
              { label: 'Speed reader',   wpm: 900,         color: '#FFD60A' },
            ].map((row) => {
              const max = Math.max(result.wpm, 900);
              const pct = Math.min(100, (row.wpm / max) * 100);
              return (
                <div key={row.label} className="text-xs">
                  <div className="flex justify-between mb-0.5">
                    <span className="text-white/70">{row.label}</span>
                    <span className="font-mono tabular-nums text-white/50">{row.wpm} wpm</span>
                  </div>
                  <div className="h-2.5 w-full bg-white/5 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-[width] duration-700"
                      style={{ width: `${pct}%`, background: row.color }}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          {/* XP badge */}
          <div
            className="w-full max-w-md rounded-2xl border border-[#FFD60A]/40 bg-[#FFD60A]/10 px-5 py-4 flex items-center gap-3"
            data-testid="speed-test-xp-badge"
          >
            <div className="w-12 h-12 rounded-full bg-[#FFD60A]/25 grid place-items-center">
              <Trophy className="w-6 h-6 text-[#FFD60A]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-widest text-[#FFD60A]/80">
                {result.credited ? 'XP credited to your account' : 'XP available'}
              </p>
              <p className="text-base font-black text-white">+{result.xp_award} XP — {result.badge}</p>
              {!result.credited && result.anonymous_cta && (
                <p className="text-[11px] text-white/60 mt-0.5">{result.anonymous_cta}</p>
              )}
              {result.new_level && (
                <p className="text-[11px] text-emerald-200 mt-0.5">Now at level {result.new_level} ✨</p>
              )}
            </div>
          </div>

          {/* CTAs */}
          <div className="w-full max-w-md grid grid-cols-1 sm:grid-cols-2 gap-2 mt-1">
            {result.credited ? (
              <button
                onClick={() => navigate(fromPostLogin ? '/profile' : '/')}
                className="bg-[#FF3B30] hover:bg-[#ff5046] text-white font-black py-3 rounded-full uppercase tracking-widest text-xs inline-flex items-center justify-center gap-2"
                data-testid="speed-test-return-app-btn"
              >
                {fromPostLogin ? (
                  <>Go to my profile <ChevronRight className="w-4 h-4" /></>
                ) : (
                  <>Train your speed on Smarter You <ChevronRight className="w-4 h-4" /></>
                )}
              </button>
            ) : (
              <button
                onClick={() => navigate('/login?signup=1')}
                className="bg-[#FF3B30] hover:bg-[#ff5046] text-white font-black py-3 rounded-full uppercase tracking-widest text-xs inline-flex items-center justify-center gap-2"
                data-testid="speed-test-signup-btn"
              >
                Sign up & save my XP <Sparkles className="w-4 h-4" />
              </button>
            )}
            <button
              onClick={startTest}
              className="border border-white/20 text-white hover:bg-white/5 font-bold py-3 rounded-full uppercase tracking-widest text-xs inline-flex items-center justify-center gap-2"
              data-testid="speed-test-retry-btn"
            >
              <RotateCw className="w-4 h-4" /> Try again
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

function shuffle(arr) {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export default SpeedTest;
